import { Alert, Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TranslateIcon from '@mui/icons-material/Translate';
import useModal from '../hooks/useModal';
import PrimaryButton from '../buttons/PrimaryButton';
import useFetch from '../hooks/useFetch';
import FlagSelect from '../common/FlagSelect';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { useNavigate, useParams } from 'react-router';
import { useContext, useState } from 'react';
import { availableLang } from '../models/Lang';
import { allowedLangOptions } from '../config/header';
import { postAutoTranslateMenu } from '../config/apiRoutes';
import SnackbarContext from '../contexts/SnackbarContext';
import UserContext from '../contexts/UserContext';
import { paymentSettingsRoute } from '../config/pageRoutes';
import ExclusiveStarIcon from '../common/ExclusiveStarIcon';
import ResponsiveModal from '../modal/ResponsiveModal';

type Props = {
    restaurantShortName: string | undefined;
    reloadMenu?: () => void;
}

const AutoTranslateButton = ({ restaurantShortName, reloadMenu }: Props) => {
    const { t } = useTranslation();
    const { lang } = useParams();
    const autoTranslateModal = useModal();
    const caller = useFetch();
    const [languageFrom, setLanguageFrom] = useState<availableLang>(lang as availableLang);
    const [selectedOptions, setSelectedOptions] = useState<availableLang[]>([]);
    const snackbar = useContext(SnackbarContext);
    const { hasFeature } = useContext(UserContext);
    const navigate = useNavigate()

    function handleAutoTranslateClick() {
        autoTranslateModal.open();
    }

    function toggleOption(option: availableLang) {
        setSelectedOptions(prev =>
            prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option]
        );
    }

    async function handleAutoTranslate() {
        if (!restaurantShortName) return;
        const url = postAutoTranslateMenu.replace(':restaurantShortName', restaurantShortName);
        const response = await caller.call("POST", url, {
            fromLanguage: languageFrom,
            toLanguage: selectedOptions
        });
        if (response.ok) {
            snackbar.showMessage(t("menu.auto_translate_success"), "success");
            reloadMenu?.();
            autoTranslateModal.close();
            setSelectedOptions([]);
        } else {
            if (response.status === 400) {
                snackbar.showMessage(t("menu.auto_translate_error_invalid_languages"), "error");
            } else if (response.status === 403) {
                snackbar.showMessage(t("menu.auto_translate_error_permission"), "error");
            } else {
                snackbar.showMessage(t("menu.auto_translate_error_generic"), "error");
            }
        }
    }

    const redirectToPayment = () => {
        navigate(paymentSettingsRoute.path.replace(':lang', lang || 'en'));
    };

    return (
        <>
            <PrimaryButton
                variant="outlined"
                onClick={handleAutoTranslateClick}
                startIcon={<TranslateIcon />}
                endIcon={
                    <div className="relative w-full h-full">
                        <ExclusiveStarIcon className="absolute -top-3 -right-5 text-white" />
                    </div>
                }
            >
                {t("menu.auto_translate")}
            </PrimaryButton>
            <ResponsiveModal
                open={autoTranslateModal.isOpen}
                handleClose={autoTranslateModal.close}
            >
                <>
                    {hasFeature("AUTO_TRANSLATE") ? (
                        <Box mb={2} px={2} py={1}>
                            <div className="font-bold text-center text-2xl text-primary-dark">
                                {t("menu.auto_translate")}
                            </div>
                            <div className="my-3 text-center md:text-left">
                                {t("menu.auto_translate_title")}
                            </div>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {t("menu.auto_translate_description")}
                            </Alert>
                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={3}>
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <Typography variant="subtitle1" gutterBottom>
                                            {t("menu.translate_from")}
                                        </Typography>
                                        <FlagSelect lang={languageFrom} setLang={setLanguageFrom} />
                                    </Box>
                                </Grid>
                                <Grid item xs={2}>
                                    <Box display="flex" flexDirection="column" alignItems="center">
                                        <ArrowRightAltIcon fontSize='large' className='text-primary' />
                                    </Box>
                                </Grid>
                                <Grid item xs={7}>
                                    <Box display="flex" flexDirection="column">
                                        <Typography variant="subtitle1" gutterBottom>
                                            {t("menu.translate_to")}
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {allowedLangOptions
                                                .filter(l => l.value !== languageFrom)
                                                .map(lang => (
                                                    <Grid item xs={12} md={6} key={lang.value}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={selectedOptions.includes(lang.value as availableLang)}
                                                                    onChange={() => toggleOption(lang.value as availableLang)}
                                                                />
                                                            }
                                                            label={
                                                                <Box display="flex" alignItems="center" gap={1}>
                                                                    <img
                                                                        src={process.env.REACT_APP_PUBLIC_URL + '/assets/flags/' + lang.iso.toLowerCase() + '.png'}
                                                                        width="20"
                                                                        height="20"
                                                                        alt={lang.label}
                                                                    />
                                                                    {t(`common.languages.${lang.iso.toLowerCase()}`)}
                                                                </Box>
                                                            }
                                                        />
                                                    </Grid>
                                                ))}
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                            <PrimaryButton
                                onClick={handleAutoTranslate}
                            >
                                {t("menu.auto_translate")}
                            </PrimaryButton>
                        </Box>
                    ) : (
                        <Box textAlign="center" mt={4}>
                            <p className="text-lg mb-4">
                                {t("menu.auto_translate_feature_not_available")}
                            </p>
                            <PrimaryButton
                                variant="primary"
                                onClick={redirectToPayment}
                            >
                                {t('menu.change_subscription_plan')}
                            </PrimaryButton>
                        </Box>
                    )}
                </>
            </ResponsiveModal>
        </>
    )
}

export default AutoTranslateButton